// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@bgs/XDL/types";
import { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Color from "color";
import { css } from "styled-components";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

const buttonSizeVariants = {
    small: {
        height: 32,
        fontSize: 12,
        letterSpacing: 0.83,
        lineHeight: 16,
        padding: "0 21px",
        icon: 12,
        iconPadding: 5,
    },
    medium: {
        height: 36,
        fontSize: 14,
        letterSpacing: 1,
        lineHeight: 18,
        padding: "0 29px",
        icon: 15,
        iconPadding: 5,
    },
    large: {
        height: 36,
        fontSize: 14,
        letterSpacing: 1,
        lineHeight: 18,
        padding: "0 30px",
        icon: 15,
        iconPadding: 5,
    },
};

const buttonPresentationStyle = (buttonType: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        buttonType: "solid",
        shape: "rectangle",
        sizeVariant: "medium",
        margeCss: true,
        css: css`
            outline: none;
            &:hover {
                border-color: ${getColor("common.white")};
            }
            &:disabled {
                opacity: 0.4;
                box-shadow: none;
            }
            &,
            &:disabled {
                && {
                    background: ${buttonType === "primary"
                        ? getColor("primary")
                        : buttonType === "secondary"
                        ? getColor("secondary")
                        : getColor("common.white")};
                    border-color: ${buttonType === "tertiary" ? getColor("text.main") : getColor("transparent")};
                }
            }

            height: auto;
            min-height: ${(props: BWT) =>
                props["_size"] || get(buttonSizeVariants, [props["_sizeVariant"], "height"])}px;
            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            &,
            * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
                font-weight: 500;
            }
            text-shadow: none;
            letter-spacing: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "letterSpacing"])}px;
            line-height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "lineHeight"])}px;
            border: 2px solid ${getColor("transparent")};
            box-shadow: 0 0 0 2px ${getColor("transparent")};
            text-transform: uppercase;

            &&& ${ButtonIcon} {
                top: 0;
            }
        `,
    };
};

export default {
    primary: {
        ...buttonPresentationStyle("primary"),
        css: [
            buttonPresentationStyle("primary").css,
            css`
                &,
                &:disabled {
                    color: ${getColor("text.main")};
                }
                &:hover:not(:disabled) {
                    color: ${getColor("text.main")};
                    background: ${getColor("primary.active")};
                }
                &:focus:not(:disabled),
                &:active:not(:disabled) {
                    outline: 4px solid ${getColor("primary.active")} !important;
                    outline-offset: -4px;
                }
            `,
        ],
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &,
                &:disabled {
                    color: ${getColor("text.main")};
                }

                &:hover:not(:disabled) {
                    color: ${getColor("text.main")};
                    background: ${getColor("secondary.active")};
                }
                &:focus:not(:disabled),
                &:active:not(:disabled) {
                    outline: 4px solid ${getColor("secondary.active")} !important;
                    outline-offset: -4px;
                }
            `,
        ],
    },
    tertiary: {
        ...buttonPresentationStyle("tertiary"),
        css: [
            buttonPresentationStyle("tertiary").css,
            css`
                &,
                &:disabled {
                    color: ${getColor("text.main")};
                    background: ${getColor("common.white")};
                }

                &:hover:not(:disabled) {
                    color: ${getColor("common.white")};
                    background: ${getColor("text.main")};
                }
                &:focus:not(:disabled),
                &:active:not(:disabled) {
                    outline: 1px solid ${getColor("text.main")} !important;
                    outline-offset: -4px;
                }
            `,
        ],
    },
} as xBaseTheme["button"];
